import { useMemo } from "react"
import PropTypes from "prop-types"
import { reduce, map, find, keys, omitBy, isEmpty } from "lodash"
import { Paper } from "@mui/material"
import Table from "components/material/table"
import { UUID, ROLE, DATASET_MEMBER } from "helpers/shapes"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"

export default function DatasetMembers({ account, role, datasetId, members, datasetMembers, update, tabs }) {
  const hasWriteAccess = role === "admin"

  const optionLabels = useMemo(() => reduce(members, (membersById, member) => ({
    ...membersById,
    [member.id]: member.name,
  }), {}),
  [members])

  const availableOptionLabels = useMemo(() =>
    omitBy(optionLabels, (_, id) => find(datasetMembers, member => member.id === id)),
  [datasetMembers, optionLabels])

  const addForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onAdd(data, cb) {
      const query = `mutation ($datasetId: String!, $memberIds: [IdOrEmail]){
          grantDatasetAccess(id: $datasetId, memberIds: $memberIds) { id }
      }`

      const variables = {
        datasetId,
        memberIds: data.members,
      }

      update(query, variables, account, {
        reloadData: ["datasetEntitlements", "memberDatasetEntitlements", "userDatasets", "models"],
      }, cb)
    }

    return {
      title: "Grant Dataset Access",
      submitText: "Grant access",
      track: {
        type: "dataset-entitlements-grant-dataset-members",
        payload: ({ members }) => ({
          members,
          datasetId,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "members",
        label: "Members",
        type: "multi",
        options: keys(availableOptionLabels),
        optionLabels: availableOptionLabels,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [hasWriteAccess, availableOptionLabels, datasetId, update, account])

  const deleteForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onDelete(data, cb) {
      const query = `mutation ($datasetId: String!, $memberIds: [IdOrEmail]){
        revokeDatasetAccess(id: $datasetId, memberIds: $memberIds) { id }
    }`
      const variables = {
        datasetId,
        memberIds: map(data, "id"),
      }

      update(query, variables, account, {
        reloadData: ["datasetEntitlements", "memberDatasetEntitlements", "userDatasets", "models"],
      }, cb)
    }

    return {
      type: "dataset-entitlement",
      track: {
        type: "dataset-entitlements-revoke-dataset-members",
        payload: (member) => ({
          member: member.id,
          datasetId,
        }),
      },
      // Only direct entitlements can be removed
      isEnabled: () => hasWriteAccess,
      renderItem: dataset => <li key={dataset.id}>{dataset.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, hasWriteAccess, datasetId, update])

  return <div className="modules_dataset-entitlements-members">
    {role === "admin" && !isEmpty(tabs) && <UpsellTabs id="dataset-members" tabs={tabs} />}

    <Paper className="content" square>
      <Table
        title="Member entitlements"
        idField="id"
        addForm={addForm}
        editForm={null}
        deleteForm={deleteForm}
        data={datasetMembers}
        columns={[{
          name: "Name",
          id: "name",
          linkTo: (member) => role === "admin" && `/${account}/home/members/${member.id}/entitlements`,
        }]}
      />
    </Paper>
  </div>
}

DatasetMembers.propTypes = {
  account: PropTypes.string.isRequired,
  datasetMembers: PropTypes.arrayOf(DATASET_MEMBER).isRequired,
  datasetId: PropTypes.string.isRequired,
  userId: UUID,
  role: ROLE.isRequired,
  members: PropTypes.array,
  update: PropTypes.func.isRequired,
  tabs: UPSELL_TABS_SHAPE,
}
